<template>
    <div class="attendance-root">
        <header class="attendance-header">
            <img src="@/assets/logogruas.png" alt="Inicio" class="logo-button" @click="goBack" />
            <div class="header-copy">
                <h2>Asistencia</h2>
                <p>{{ isRemote ? 'Registra tu entrada y salida desde el sistema' : 'Consulta tu asistencia del día' }}</p>
            </div>
        </header>

        <section class="clock-card">
            <span class="clock-label">Hora del sistema</span>
            <div class="clock-time">{{ clockText }}</div>
            <div class="clock-date">{{ clockDateText }}</div>
            <div class="clock-zone">{{ timezoneName }}</div>
        </section>

        <section class="punch-card">
            <template v-if="isRemote">
                <button
                    class="punch-btn"
                    :disabled="!canPunch"
                    @click="reportTime"
                >
                    {{ punchButtonLabel }}
                </button>
                <p class="punch-hint">{{ punchHint }}</p>
                <p v-if="cooldownSeconds > 0" class="punch-cooldown">
                    Podrás registrar otro movimiento en {{ cooldownSeconds }} s.
                </p>
                <p v-if="locationNotice" class="punch-location-notice">{{ locationNotice }}</p>
            </template>
            <p v-else class="punch-hint">
                Tu asistencia se registra en el checador. Aquí solo puedes consultar tu día.
            </p>
        </section>

        <section v-if="successMessage" class="success-box">{{ successMessage }}</section>
        <section v-if="error" class="error-box">{{ error }}</section>

        <section class="day-card">
            <div class="day-card-head">
                <h3>Mi día</h3>
                <button class="link-btn" @click="fetchState" :disabled="loading">
                    {{ loading ? 'Actualizando...' : 'Actualizar' }}
                </button>
            </div>
            <div class="day-grid">
                <div class="day-block">
                    <span class="day-label">Entrada</span>
                    <div class="day-value">{{ record.clock_in || '--:--' }}</div>
                    <span class="day-location">{{ record.clock_in_location || 'Sin ubicación' }}</span>
                </div>
                <div class="day-block">
                    <span class="day-label">Salida</span>
                    <div class="day-value">{{ record.clock_out || '--:--' }}</div>
                    <span class="day-location">{{ record.clock_out_location || 'Sin ubicación' }}</span>
                </div>
            </div>
            <p v-if="isRemote" class="day-note">
                El primer movimiento del día queda como entrada y el último como salida.
                Si tu turno cruza la medianoche, la salida se registra en el día siguiente.
            </p>
            <p v-else class="day-note">
                Estas horas vienen del checador. Si algo no coincide, avisa a Recursos Humanos.
            </p>
        </section>

        <!-- Only remote staff can produce these; the list would always be empty otherwise. -->
        <section v-if="isRemote" class="punches-card">
            <h3>Movimientos de hoy</h3>
            <ul v-if="punches.length" class="punches-list">
                <li v-for="punch in punches" :key="punch.id" class="punch-item">
                    <span class="punch-time">{{ punch.time }}</span>
                    <span class="punch-location">{{ punch.location || 'Sin ubicación' }}</span>
                </li>
            </ul>
            <p v-else class="punches-empty">Aún no has registrado movimientos hoy.</p>
        </section>

        <section class="footer-actions">
            <button class="btn btn-secondary" @click="goBack">Regresar</button>
        </section>
    </div>
</template>

<script>
import axios from 'axios'

const WEEKDAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
const MONTH_NAMES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

export default {
    name: 'OperatorAttendance',
    data() {
        return {
            loading: false,
            saving: false,
            error: '',
            successMessage: '',
            locationNotice: '',
            // Punches are timestamped by the backend, so the displayed clock is
            // driven by server time instead of the device clock.
            serverOffsetMs: 0,
            now: new Date(),
            serverDate: '',
            timezoneName: '',
            isRemote: false,
            nextPunchAllowedAt: '',
            record: {
                status_code: '-',
                clock_in: '',
                clock_out: '',
                clock_in_location: '',
                clock_out_location: '',
            },
            punches: [],
            tickHandle: null,
        }
    },
    computed: {
        clockText() {
            return [
                String(this.now.getHours()).padStart(2, '0'),
                String(this.now.getMinutes()).padStart(2, '0'),
                String(this.now.getSeconds()).padStart(2, '0'),
            ].join(':')
        },
        clockDateText() {
            const weekday = WEEKDAY_NAMES[this.now.getDay()]
            const month = MONTH_NAMES[this.now.getMonth()]
            return `${weekday} ${this.now.getDate()} de ${month} de ${this.now.getFullYear()}`
        },
        cooldownSeconds() {
            if (!this.nextPunchAllowedAt) return 0
            const target = new Date(this.nextPunchAllowedAt).getTime()
            if (Number.isNaN(target)) return 0
            const remaining = Math.ceil((target - this.now.getTime()) / 1000)
            return remaining > 0 ? remaining : 0
        },
        canPunch() {
            return this.isRemote && !this.saving && !this.loading && this.cooldownSeconds === 0
        },
        punchButtonLabel() {
            if (this.saving) return 'Registrando...'
            if (this.cooldownSeconds > 0) return `Espera ${this.cooldownSeconds} s`
            return 'Registrar hora'
        },
        punchHint() {
            if (!this.record.clock_in) return 'Este movimiento quedará como tu entrada del día.'
            if (!this.record.clock_out) return 'Este movimiento quedará como tu salida del día.'
            return 'Este movimiento reemplazará tu hora de salida del día.'
        },
    },
    mounted() {
        this.fetchState()
        this.tickHandle = setInterval(this.tick, 1000)
    },
    beforeUnmount() {
        if (this.tickHandle) clearInterval(this.tickHandle)
    },
    methods: {
        apiBase() {
            return process.env.API_DIRECTORY || ''
        },
        authHeaders() {
            return { Authorization: `Token ${localStorage.getItem('authToken')}` }
        },
        tick() {
            const previousDate = this.now.toDateString()
            this.now = new Date(Date.now() + this.serverOffsetMs)
            // A day switch resets the record the punches belong to.
            if (this.now.toDateString() !== previousDate) this.fetchState()
        },
        applyState(data) {
            if (data?.server_time) {
                const serverTime = new Date(data.server_time).getTime()
                if (!Number.isNaN(serverTime)) {
                    this.serverOffsetMs = serverTime - Date.now()
                    this.now = new Date(Date.now() + this.serverOffsetMs)
                }
            }
            this.serverDate = data?.server_date || ''
            this.timezoneName = data?.timezone || ''
            this.isRemote = !!data?.is_remote
            this.nextPunchAllowedAt = data?.next_punch_allowed_at || ''
            this.record = {
                status_code: data?.record?.status_code || '-',
                clock_in: data?.record?.clock_in || '',
                clock_out: data?.record?.clock_out || '',
                clock_in_location: data?.record?.clock_in_location || '',
                clock_out_location: data?.record?.clock_out_location || '',
            }
            this.punches = Array.isArray(data?.punches) ? data.punches : []
        },
        async fetchState() {
            this.loading = true
            this.error = ''
            try {
                const { data } = await axios.get(`${this.apiBase()}/my-attendance`, {
                    headers: this.authHeaders(),
                })
                this.applyState(data)
            } catch (error) {
                this.error = error?.response?.status === 403
                    ? 'Tu usuario no está ligado a un expediente de personal. Avisa a Recursos Humanos.'
                    : 'No se pudo cargar tu asistencia.'
            } finally {
                this.loading = false
            }
        },
        currentPosition() {
            return new Promise((resolve) => {
                if (!navigator.geolocation) {
                    resolve(null)
                    return
                }
                navigator.geolocation.getCurrentPosition(
                    (position) => resolve(position),
                    () => resolve(null),
                    { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
                )
            })
        },
        async reportTime() {
            if (!this.canPunch) return
            this.saving = true
            this.error = ''
            this.successMessage = ''
            this.locationNotice = 'Obteniendo ubicación...'

            const position = await this.currentPosition()
            this.locationNotice = position
                ? ''
                : 'No se pudo obtener tu ubicación; el movimiento se registró sin ella.'

            const payload = {}
            if (position) {
                payload.latitude = position.coords.latitude
                payload.longitude = position.coords.longitude
                payload.accuracy = position.coords.accuracy
            }

            try {
                const { data } = await axios.post(
                    `${this.apiBase()}/my-attendance-punch`,
                    payload,
                    { headers: this.authHeaders() }
                )
                this.applyState(data)
                this.successMessage = `Movimiento registrado a las ${data?.punch?.time || this.clockText}.`
            } catch (error) {
                const status = error?.response?.status
                if (status === 429) {
                    this.nextPunchAllowedAt = error.response.data?.next_punch_allowed_at || ''
                    this.error = error.response.data?.error || 'Espera unos segundos antes de registrar otro movimiento.'
                } else if (status === 403) {
                    this.error = error.response.data?.code === 'not_remote'
                        ? 'Solo el personal remoto puede registrar asistencia desde el sistema.'
                        : 'Tu usuario no está ligado a un expediente de personal. Avisa a Recursos Humanos.'
                } else {
                    this.error = 'No se pudo registrar tu movimiento. Intenta de nuevo.'
                }
                this.locationNotice = ''
            } finally {
                this.saving = false
            }
        },
        goBack() {
            this.$router.push({ name: 'OperatorInterface' })
        },
    },
}
</script>

<style scoped>
.attendance-root {
    min-height: 100vh;
    padding: 12px 12px 22px;
    background: linear-gradient(180deg, #fff7df 0%, #fff 26%);
    color: #1a1a1a;
    display: grid;
    gap: 12px;
    align-content: start;
}

.attendance-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 16px;
    background: #ffc434;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

/* Several components ship an unscoped rule that absolutely centers
   .logo-button on screen; it leaks in once any of them has been visited. */
.logo-button {
    position: static !important;
    top: auto !important;
    left: auto !important;
    transform: none !important;
    display: block;
    width: 70px;
    height: 70px;
    object-fit: contain;
    background: #fff;
    border-radius: 14px;
    padding: 8px;
    cursor: pointer;
}

.header-copy h2 {
    margin: 0;
    font-size: 1.2rem;
}

.header-copy p {
    margin: 3px 0 0;
    font-size: 0.85rem;
    opacity: 0.8;
}

.clock-card {
    padding: 18px 14px;
    border-radius: 16px;
    background: #0f172a;
    color: #fff;
    text-align: center;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.24);
}

.clock-label {
    display: block;
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #ffd873;
}

.clock-time {
    margin-top: 6px;
    font-size: 3.1rem;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
}

.clock-date {
    margin-top: 8px;
    font-size: 0.92rem;
    text-transform: capitalize;
}

.clock-zone {
    margin-top: 2px;
    font-size: 0.74rem;
    color: #94a3b8;
}

.punch-card {
    padding: 14px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
    text-align: center;
}

.punch-btn {
    width: 100%;
    border: none;
    border-radius: 14px;
    padding: 20px 14px;
    background: #ffc434;
    color: #0f172a;
    font-size: 1.12rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    cursor: pointer;
    box-shadow: 0 10px 20px rgba(255, 196, 52, 0.4);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.punch-btn:active:not(:disabled) {
    transform: scale(0.985);
}

.punch-btn:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    box-shadow: none;
    cursor: not-allowed;
}

.punch-hint {
    margin: 10px 0 0;
    font-size: 0.84rem;
    color: #475569;
}

.punch-cooldown,
.punch-location-notice {
    margin: 6px 0 0;
    font-size: 0.78rem;
    color: #854d0e;
}

.day-card,
.punches-card {
    padding: 14px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.day-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.day-card h3,
.punches-card h3 {
    margin: 0;
    font-size: 0.96rem;
}

.link-btn {
    border: 1px solid #d4a62a;
    background: #fff4d2;
    color: #1e293b;
    border-radius: 999px;
    padding: 5px 11px;
    font-size: 0.78rem;
    font-weight: 700;
    cursor: pointer;
}

.link-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.day-grid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.day-block {
    border: 1px solid #ddbf66;
    border-radius: 12px;
    padding: 10px;
    background: rgba(255, 247, 223, 0.7);
    min-width: 0;
}

.day-label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
}

.day-value {
    margin-top: 2px;
    font-size: 1.5rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: #0f172a;
}

.day-location {
    display: block;
    margin-top: 2px;
    font-size: 0.72rem;
    color: #475569;
    overflow-wrap: anywhere;
}

.day-note {
    margin: 12px 0 0;
    font-size: 0.78rem;
    line-height: 1.4;
    color: #64748b;
}

.punches-list {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 6px;
}

.punch-item {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
}

.punch-time {
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: #0f172a;
}

.punch-location {
    font-size: 0.76rem;
    color: #475569;
    overflow-wrap: anywhere;
}

.punches-empty {
    margin: 12px 0 0;
    font-size: 0.82rem;
    color: #64748b;
}

.success-box,
.error-box {
    padding: 11px 12px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    font-size: 0.86rem;
}

.success-box {
    color: #166534;
    border-color: #bbf7d0;
}

.error-box {
    color: #b91c1c;
    border-color: #fecaca;
}

.footer-actions {
    display: grid;
    gap: 8px;
}

.btn {
    border: none;
    border-radius: 10px;
    padding: 10px 12px;
    font-weight: 600;
    font-size: 0.88rem;
    cursor: pointer;
}

.btn-secondary {
    background: #fff4d2;
    color: #1e293b;
    border: 1px solid #d4a62a;
}

@media (min-width: 768px) {
    .attendance-root {
        padding: 18px 20px 28px;
        max-width: 720px;
        margin: 0 auto;
    }

    .clock-time {
        font-size: 3.8rem;
    }
}
</style>
